/**
 * Ported VERBATIM from `apr_template/src/pages/ai-analysis/AnalysisRepository.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';
import { useAuth } from '../compat/AuthContext';
import {
  type RepositoryRow,
  type AppAnalyses,
  type AnalysisHistoryItem,
  getAnalysisRepository,
  getAppAnalyses,
  pinAnalysis,
  unpinAnalysis,
  pinAnalysisAsFinal,
  unpinAnalysisAsFinal,
  recordFinalFromPinned,
  askAiWhichIsRealistic,
  getSavedComparison,
  type CompareVerdict,
} from '../compat/aiRepositoryApi';
import { getPortfolioCurrent, downloadReport, type CurrentReport } from '../compat/reportsApi';
import ScoringDetailsPanel from '../components/ScoringDetailsPanel';

/* ---- helpers ---- */
// Postgres numeric/decimal columns arrive as strings — coerce before math.
const num = (v: any): number => { const n = Number(v); return isNaN(n) ? 0 : n; };
const fmt$ = (n?: number | string | null) =>
  n == null || n === '' || isNaN(Number(n)) ? '—' : `$${Math.round(Number(n)).toLocaleString('en-US')}`;
const fmtCostRange = (mid?: any, low?: any, high?: any): string => {
  const has = (low != null && low !== '') || (high != null && high !== '');
  if (!has) return fmt$(mid);
  const base = `${fmt$(low)} – ${fmt$(high)}`;
  return mid != null && mid !== '' ? `${base} · mid ${fmt$(mid)}` : base;
};
const fmtNum = (n?: number | string | null) => (n == null ? '0' : Math.round(Number(n) || 0).toLocaleString('en-US'));

/**
 * Screens that can deep-link into this one (?from=…). Closing the panel returns to
 * whichever sent you, so you land back where you started rather than being stranded
 * on a screen you never chose to open. A token map, not a raw path in the URL, so a
 * crafted link can't redirect anywhere it likes.
 */
const ORIGIN_SCREENS: Record<string, string> = {
  // The PLATFORM route, not the source's /customers/applications. This map is how the
  // panel's close button gets back to where the user started; pointed at the source path
  // it returned them to "Module not available".
  portfolio: '/apr/portfolio/applications',
};

const filterLabel: React.CSSProperties = {
  display: 'block', fontSize: 10.5, fontWeight: 700, color: '#90a4ae',
  textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
};

/**
 * Does this app's set of analyses materially disagree — i.e. is a human decision owed?
 * Deterministic, no AI. Two signals:
 *   - they land on different 4R dispositions, or
 *   - their total one-time cost differs by more than half.
 * Cost is the one that bites: two analyses can agree on the call and the savings and
 * still be hundreds of thousands apart on what it takes to get there. Because an
 * unpinned app resolves to "latest wins", a new run can swap that silently.
 */
const ONE_TIME_COST_SPREAD = 0.5;
function disagreement(r: RepositoryRow): { why: string } | null {
  if (num(r.analyses_count) < 2) return null;
  const reasons: string[] = [];
  if (r.disposition_differs) reasons.push('they recommend different dispositions');
  const lo = r.one_time_cost_min == null ? null : num(r.one_time_cost_min);
  const hi = r.one_time_cost_max == null ? null : num(r.one_time_cost_max);
  if (lo != null && hi != null && hi > 0 && (hi - lo) / hi > ONE_TIME_COST_SPREAD) {
    reasons.push(`one-time cost ranges ${fmt$(lo)} – ${fmt$(hi)}`);
  }
  if (!reasons.length) return null;
  return { why: `${r.analyses_count} analyses disagree: ${reasons.join('; ')}. Open the app to compare them.` };
}

// Consistent disposition palette across the repository.
function dispColor(d?: string | null): string {
  const key = (d || '').toUpperCase();
  if (key.includes('RETIRE')) return '#c62828';
  if (key.includes('REPLACE')) return '#e64a19';
  if (key.includes('CONSOLIDAT')) return '#7b1fa2';
  if (key.includes('CLOUD')) return '#00838f';
  if (key.includes('MODERNIZE') || key.includes('RE-DESIGN')) return '#4527a0';
  if (key.includes('RENEGOT')) return '#f57f17';
  if (key.includes('RIGHT') || key.includes('SIZE')) return '#00838f';
  if (key.includes('RETAIN')) return '#2e7d32';
  if (key.includes('INSUFFICIENT')) return '#b45309';
  if (key.includes('REVIEW')) return '#757575';
  return '#546e7a';
}
function dispLabel(d?: string | null): string {
  if (!d) return 'No disposition';
  const k = d.toUpperCase();
  if (k === 'INSUFFICIENT_DATA') return 'Insufficient Data';
  if (k === 'NEEDS_REVIEW') return 'Needs Review';
  return d;
}
/** Coarse 4R parent of a rich disposition label — used to compare an analysis against
 *  the Q&A answer (which is one of the four parents). Mirrors the server's derive4RParent. */
function parent4R(label?: string | null): string | null {
  if (!label) return null;
  const k = String(label).toUpperCase();
  if (k.includes('RETIRE')) return 'Retire';
  if (k.includes('RE-DESIGN') || k.includes('MODERNIZE')) return 'Re-Design';
  if (k.includes('CONSOLIDAT') || k.includes('CLOUD') || k.includes('REPLACE') || k === 'RE-PLATFORM') return 'Re-Platform';
  if (k.includes('RETAIN') || k.includes('RENEGOT') || k.includes('RIGHT')) return 'Retain';
  return null;
}

function DispPill({ d, solid }: { d?: string | null; solid?: boolean }) {
  const c = dispColor(d);
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
      padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap',
      color: solid ? '#fff' : c,
      background: solid ? c : `${c}18`,
      border: `1px solid ${solid ? c : `${c}55`}`,
    }}>{dispLabel(d)}</span>
  );
}

// Provenance of an analysis: AI / Client / Merged / Manual.
const ORIGIN_TAG: Record<string, { label: string; c: string; title: string }> = {
  ai_generated:    { label: 'Baseline Assessment', c: '#3538cd', title: 'Baseline assessment generated by the Claude API' },
  client_document: { label: 'Client', c: '#17914f', title: 'Interpreted from a client-provided document' },
  consolidated:    { label: 'Merged', c: '#7b1fa2', title: 'Consolidated: client + existing AI analysis' },
  manual:          { label: 'Manual', c: '#55627a', title: 'Manually entered analysis' },
};
function OriginTag({ origin }: { origin?: string }) {
  const s = ORIGIN_TAG[origin || 'ai_generated'] || ORIGIN_TAG.ai_generated!;
  return (
    <span title={s.title} style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap', color: s.c, background: `${s.c}18`, border: `1px solid ${s.c}55` }}>
      {s.label}
    </span>
  );
}

const RepoHelp: Help = {
  title: 'Analysis Repository',
  icon: '🗂️',
  purpose: 'The single source of truth for where every analyzed application landed. One row per application showing its CURRENT AI disposition, the savings behind it, and the full history of every analysis run for that app.',
  doHere: [
    'Scan the portfolio-wide <b>current disposition</b> for each application at a glance.',
    'Click an application to open its <b>analysis history</b> — every run that assessed it.',
    '<b>Pin</b> a specific analysis as the current one, or leave it on auto-latest.',
    'Jump to the source run, or open the application in Application Master Data.',
  ],
  fits: 'Application Evaluation &rarr; Disposition Intelligence &rarr; Analysis Repository',
  firstTime: 'Run an analysis from Run Analysis; every completed analysis is recorded here automatically.',
};

/* ============================================================= */
/* Main                                                          */
/* ============================================================= */
const AnalysisRepository: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const appIdParam = searchParams.get('appId'); // deep-link from Application Master Data
  const fromParam = searchParams.get('from');   // which screen sent us here
  // Where closing the panel should return to. Null = we were already on this screen,
  // so closing just closes the panel and stays put.
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const { currentUser } = useAuth();
  const canPin = currentUser?.role === 'Administrator' || currentUser?.role === 'APR Analyst';

  const [rows, setRows] = useState<RepositoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [dispFilter, setDispFilter] = useState<string>('');
  // Last-analyzed range (yyyy-mm-dd from the native date pickers; either end optional).
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [openApp, setOpenApp] = useState<RepositoryRow | null>(null);
  const [report, setReport] = useState<CurrentReport>({ artifact: null, stale: false });
  const [dlBusy, setDlBusy] = useState('');
  const [exportError, setExportError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await getAnalysisRepository());
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load the analysis repository.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // The cached portfolio deliverable (download-only here; managed in Report Library).
    getPortfolioCurrent().then(setReport).catch(() => { /* non-blocking */ });
  }, []);

  // Deep-link ?appId=<id> (from Application Master Data → Disposition Analysis) →
  // select that application and open its panel, exactly as if the row was clicked.
  // One-shot: the param is consumed so a reload doesn't force the panel back open
  // after the user closes it.
  const consumedAppIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!appIdParam || !rows.length || consumedAppIdRef.current === appIdParam) return;
    consumedAppIdRef.current = appIdParam;
    const match = rows.find((r) => r.app_id === appIdParam);
    if (match) setOpenApp(match);
    // Remember the origin BEFORE the params are dropped from the URL. Carry the app id
    // back so the origin screen reopens on the same application the user left from,
    // rather than dumping them at the top of a 576-row list.
    if (fromParam && ORIGIN_SCREENS[fromParam]) {
      setReturnTo(`${ORIGIN_SCREENS[fromParam]}?appId=${encodeURIComponent(appIdParam)}`);
    }
    setSearchParams({}, { replace: true });
  }, [appIdParam, fromParam, rows, setSearchParams]);

  // Aggregates
  const totals = useMemo(() => {
    const savings = rows.reduce((s, r) => s + num(r.savings_mid), 0);
    const inScope = rows.reduce((s, r) => s + num(r.current_annual_cost), 0);
    const analyses = rows.reduce((s, r) => s + num(r.analyses_count), 0);
    const byDisp: Record<string, number> = {};
    rows.forEach((r) => { const k = (r.disposition || 'No disposition').toUpperCase(); byDisp[k] = (byDisp[k] || 0) + 1; });
    const dist = Object.entries(byDisp).sort((a, b) => b[1] - a[1]);
    // The decisions this screen exists to drive: what's been committed, and where a
    // choice is actually owed. Neither was visible anywhere before.
    const pinned = rows.filter((r) => r.pinned).length;
    const needsDecision = rows.filter((r) => !!disagreement(r)).length;
    const clientSourced = rows.filter((r) => r.origin === 'client_document' || r.origin === 'consolidated').length;
    return { savings, inScope, analyses, dist, pinned, needsDecision, clientSourced };
  }, [rows]);

  const dispositions = useMemo(
    () => Array.from(new Set(rows.map((r) => (r.disposition || '').toUpperCase()).filter(Boolean))),
    [rows]
  );

  const filtered = rows.filter((r) => {
    if (dispFilter && (r.disposition || '').toUpperCase() !== dispFilter) return false;
    // Last-analyzed range. Compare on the calendar date only, so a run late on the
    // "to" day still matches — an end date should mean the whole of that day.
    if (fromDate || toDate) {
      if (!r.analyzed_at) return false;
      const day = new Date(r.analyzed_at).toISOString().slice(0, 10);
      if (fromDate && day < fromDate) return false;
      if (toDate && day > toDate) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (r.application_name || '').toLowerCase().includes(s) ||
      (r.application_id_code || '').toLowerCase().includes(s) ||
      (r.vendor || '').toLowerCase().includes(s) ||
      (r.disposition || '').toLowerCase().includes(s)
    );
  });

  // Download-only: the portfolio deliverable is generated + versioned in the
  // Report Library. Here we just let users grab the current cached document.
  const doDownload = useCallback(async (fmt: 'docx' | 'pptx') => {
    if (!report.artifact) return;
    setDlBusy(fmt); setExportError('');
    try { await downloadReport(report.artifact.id, fmt); }
    catch (e: any) { setExportError(e?.response?.data?.error || e?.message || 'Download failed.'); }
    finally { setDlBusy(''); }
  }, [report.artifact]);

  const headerActions = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      {report.artifact ? (
        <>
          <button className="rd-btn rd-btn-word" onClick={() => doDownload('docx')} disabled={!!dlBusy || !report.artifact.docx_key}
            title={`Download the portfolio summary (Word) — generated ${new Date(report.artifact.generated_at).toLocaleDateString()}`}>
            <i className="material-icons">description</i>{dlBusy === 'docx' ? 'Downloading…' : 'Download Word'}
          </button>
          <button className="rd-btn rd-btn-ppt" onClick={() => doDownload('pptx')} disabled={!!dlBusy || !report.artifact.pptx_key}
            title={`Download the portfolio summary (PowerPoint) — generated ${new Date(report.artifact.generated_at).toLocaleDateString()}`}>
            <i className="material-icons">slideshow</i>{dlBusy === 'pptx' ? 'Downloading…' : 'Download PPT'}
          </button>
        </>
      ) : (
        <span style={{ fontSize: 12, color: '#90a4ae', alignSelf: 'center' }}>Report not generated yet</span>
      )}
      <button className="rd-btn rd-btn-ghost" onClick={() => navigate('/apr/application-evaluation/disposition-intelligence/report-library')} title="Open the Report Library to generate or view the report history">
        <i className="material-icons">menu_book</i>Report Library
      </button>
      <button className="rd-btn rd-btn-ghost" onClick={load} disabled={loading}>
        <i className="material-icons">refresh</i>{loading ? 'Loading…' : 'Refresh'}
      </button>
    </div>
  );

  return (
    <div className="page ai-results-page page-fixed-header">
      <HelpPanel help={RepoHelp} />
      <div className="fixed-header-section">
      <PageHeader
        title="Analysis Repository"
        subtitle="The current disposition for every application we've analyzed — with the full history behind each."
        actions={headerActions}
      />

      {loading && rows.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9e9e9e' }}>Loading repository…</div>
      )}
      {error && (
        <div style={{ margin: 20, padding: '14px 18px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 8, color: '#c62828' }}>{error}</div>
      )}
      {exportError && (
        <div style={{ margin: '0 20px 12px', padding: '12px 16px', background: '#fff4e5', border: '1px solid #ffcc80', borderRadius: 8, color: '#b45309', display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="material-icons" style={{ fontSize: 18 }}>error_outline</i>
          <span>Export failed: {exportError}</span>
          <button className="rd-btn rd-btn-ghost" style={{ marginLeft: 'auto', fontSize: 12 }} onClick={() => setExportError('')}>Dismiss</button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div style={{ margin: 20, padding: 40, textAlign: 'center', color: '#78909c', background: '#f8fafc', border: '1px dashed #cfd8dc', borderRadius: 12 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🗂️</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#455a64' }}>No analyses yet</div>
          <div style={{ marginTop: 6 }}>Run a portfolio analysis from <strong>Run Analysis</strong> — completed analyses appear here automatically.</div>
          <button className="rd-btn rd-btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/apr/application-evaluation/disposition-intelligence/run-analysis')}>
            <i className="material-icons">auto_awesome</i>Go to Run Analysis
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <>
          {/* One row: the headline savings sits WITH the decision stats rather than in a
              ribbon of its own. Same card height throughout; only the widths differ, so
              the row reads as one band. */}
          <div className="rd-stat-grid" style={{ margin: '4px 20px 16px' }}>
            {[
              {
                icon: 'savings', color: 'purple', label: `${totals.inScope > 0 ? Math.round((totals.savings / totals.inScope) * 100) : 0}% of ${fmt$(totals.inScope)} in-scope · ${rows.length} apps`,
                value: fmt$(totals.savings),
                valueLabel: 'Total current annual savings',
                highlight: true,
                title: `Total current annual savings identified across ${rows.length} analyzed applications, against ${fmt$(totals.inScope)} of in-scope spend.`,
              },
              {
                icon: 'push_pin', color: 'green', label: 'Committed (pinned)',
                value: `${fmtNum(totals.pinned)} of ${fmtNum(rows.length)}`,
                title: 'Applications whose current analysis has been explicitly pinned. The rest fall back to their latest analysis, which a future run can silently replace.',
              },
              {
                icon: 'help_outline', color: 'orange', label: 'Awaiting a decision',
                value: fmtNum(rows.length - totals.pinned),
                title: 'Analyzed applications with nothing pinned — no one has committed to which analysis is current.',
              },
              {
                icon: 'warning_amber', color: 'red', label: 'Analyses disagree',
                value: fmtNum(totals.needsDecision),
                title: 'Applications whose analyses reach different dispositions, or differ by more than half on one-time cost. These are where a choice actually matters — look for the ⚠ differ badge.',
              },
              {
                icon: 'history', color: 'purple', label: 'Analyses on Record',
                value: fmtNum(totals.analyses),
                title: `${fmtNum(totals.clientSourced)} application${totals.clientSourced === 1 ? '' : 's'} currently sit on a client-provided or consolidated analysis rather than a baseline AI one.`,
              },
            ].map((s: any) => (
              <div
                key={s.label}
                className="rd-stat-card"
                title={s.title}
                style={s.highlight
                  // The savings card keeps the brand gradient so it still reads as the
                  // headline, without needing a whole ribbon to say so.
                  ? { cursor: 'help', gridColumn: 'span 2', background: 'linear-gradient(120deg, #4f46e5 0%, #7c3aed 55%, #9333ea 100%)', border: 'none', color: '#fff' }
                  : { cursor: 'help' }}
              >
                <div className={`rd-stat-card-icon ${s.color}`} style={s.highlight ? { background: 'rgba(255,255,255,0.18)', color: '#fff' } : undefined}>
                  <i className="material-icons">{s.icon}</i>
                </div>
                <div className="rd-stat-card-body">
                  {s.valueLabel && (
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.85 }}>
                      {s.valueLabel}
                    </div>
                  )}
                  <div className="rd-stat-card-value" style={s.highlight ? { color: '#fff' } : undefined}>{s.value}</div>
                  <div className="rd-stat-card-label" style={s.highlight ? { color: 'rgba(255,255,255,0.9)' } : undefined}>{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Disposition distribution */}
          <div className="rd-card" style={{ margin: '0 20px 16px', padding: '14px 18px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#607d8b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Portfolio disposition mix</div>
            <div style={{ display: 'flex', height: 16, borderRadius: 8, overflow: 'hidden', boxShadow: 'inset 0 0 0 1px #eceff1' }}>
              {totals.dist.map(([d, n]) => (
                <div key={d} title={`${d}: ${n}`} style={{ flex: n, background: dispColor(d), minWidth: 2 }} />
              ))}
            </div>
            {/* One row, always — the legend is a glance, not a paragraph. It scrolls
                sideways rather than wrapping to a second line and pushing the table
                down. Type is sized down to fit the longer labels. */}
            <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 8, marginTop: 10, overflowX: 'auto', paddingBottom: 2 }}>
              {totals.dist.map(([d, n]) => (
                <button
                  key={d}
                  onClick={() => setDispFilter(dispFilter === d ? '' : d)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                    background: dispFilter === d ? `${dispColor(d)}14` : 'transparent',
                    border: `1px solid ${dispFilter === d ? dispColor(d) : 'transparent'}`,
                    borderRadius: 20, padding: '2px 8px', fontSize: 10.5, fontFamily: 'inherit',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                  title={`${dispLabel(d)} — ${n} application${n === 1 ? '' : 's'}. Click to filter.`}
                >
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: dispColor(d), flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, color: '#37474f' }}>{d}</span>
                  <span style={{ color: '#90a4ae' }}>{n} · {Math.round((n / rows.length) * 100)}%</span>
                </button>
              ))}
            </div>
          </div>

          {/* Filters — three, each only as wide as its content needs. */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, margin: '0 20px 10px', flexWrap: 'wrap' }}>
            <div>
              <label style={filterLabel}>Application, App ID or vendor</label>
              <input
                type="text"
                placeholder="🔍 Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rd-input"
                style={{ width: 260, padding: '8px 12px' }}
              />
            </div>

            <div>
              <label style={filterLabel}>Current disposition</label>
              <select
                className="rd-select"
                value={dispFilter}
                onChange={(e) => setDispFilter(e.target.value)}
                style={{ width: 200, padding: '8px 10px' }}
              >
                <option value="">All dispositions</option>
                {dispositions.map((d) => (
                  <option key={d} value={d}>{dispLabel(d)}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={filterLabel}>Last analyzed</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="rd-input"
                  title="Analyzed on or after this date"
                  style={{ width: 148, padding: '7px 10px' }}
                />
                <span style={{ fontSize: 12, color: '#90a4ae' }}>to</span>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="rd-input"
                  title="Analyzed on or before this date"
                  style={{ width: 148, padding: '7px 10px' }}
                />
              </div>
            </div>

            {(search || dispFilter || fromDate || toDate) && (
              <button
                className="rd-btn rd-btn-ghost"
                onClick={() => { setSearch(''); setDispFilter(''); setFromDate(''); setToDate(''); }}
                style={{ fontSize: 12 }}
              >
                Clear filters ✕
              </button>
            )}
            <span style={{ fontSize: 12, color: '#90a4ae', marginLeft: 'auto', paddingBottom: 8 }}>
              {filtered.length} of {rows.length}
            </span>
          </div>

        </>
      )}
      </div>

      {/* Only this section scrolls. The header, stat cards and filters above stay put;
          the table's own column headers stick to the top of this box. */}
      <div className="scrollable-data-section">
        {/* No overflow on the div below: `overflow-x: auto` would make IT the scrollport,
            and the sticky column headers would pin to it and scroll away with it.
            Horizontal scrolling lives on .scrollable-data-section instead. */}
        {rows.length > 0 && (
          <div style={{ margin: '0 20px 24px', display: 'flex', flexDirection: 'column', width: '100%', minWidth: 0 }}>
            <table className="rd-table repo-table">
              <thead>
                <tr>
                  <th>App ID</th>
                  <th>Application</th>
                  <th>Vendor</th>
                  <th>Current Disposition</th>
                  <th>Source</th>
                  <th>Confidence</th>
                  <th style={{ textAlign: 'right' }}>Current $</th>
                  <th style={{ textAlign: 'right' }}>Savings (Mid)</th>
                  <th style={{ textAlign: 'center' }}>Analyses</th>
                  <th>Last Analyzed</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={10} style={{ textAlign: 'center', color: '#9e9e9e', padding: 32 }}>No applications match the current filters.</td></tr>
                )}
                {filtered.map((r) => (
                  <tr
                    key={r.app_id}
                    onClick={() => setOpenApp(r)}
                    // Highlight the selected row so it's clear which application the
                    // panel belongs to — especially when arriving via a deep link.
                    style={openApp?.app_id === r.app_id
                      ? { cursor: 'pointer', background: '#e8f0fe', boxShadow: 'inset 3px 0 0 #1565c0' }
                      : { cursor: 'pointer' }}
                  >
                    <td style={{ fontWeight: 700, color: '#1565c0', whiteSpace: 'nowrap' }}>
                      {r.application_id_code || '—'}
                      {r.pinned && <span title="Pinned as current" style={{ marginLeft: 6 }}>📌</span>}
                    </td>
                    <td><span style={{ fontWeight: 500 }}>{r.application_name}</span></td>
                    <td className="rd-cell-muted">{r.vendor || '—'}</td>
                    <td><DispPill d={r.disposition} /></td>
                    <td><OriginTag origin={r.origin} /></td>
                    <td className="rd-cell-muted" style={{ textTransform: 'capitalize' }}>{r.confidence || '—'}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{fmt$(r.current_annual_cost)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'monospace', color: '#2e7d32', fontWeight: 600 }}>{fmt$(r.savings_mid)}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#5e35b1', background: '#ede7f6', borderRadius: 20, padding: '2px 9px' }}>{r.analyses_count}</span>
                      {(() => {
                        const d = disagreement(r);
                        return d ? (
                          <span
                            title={d.why}
                            style={{ marginLeft: 5, fontSize: 10, fontWeight: 800, color: '#8a6d00', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 20, padding: '2px 7px', cursor: 'help' }}
                          >
                            ⚠ differ
                          </span>
                        ) : null;
                      })()}
                    </td>
                    <td className="rd-cell-muted">{r.analyzed_at ? new Date(r.analyzed_at).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openApp && (
        <AppHistoryDrawer
          appId={openApp.app_id}
          canPin={canPin}
          // Closing returns to the screen that sent us here (e.g. Application Master
          // Data). Opened from this screen's own list, it just closes the panel.
          onClose={() => {
            setOpenApp(null);
            if (returnTo) {
              const dest = returnTo;
              setReturnTo(null);
              navigate(dest);
            }
          }}
          onChanged={load}
        />
      )}
    </div>
  );
};

/* ============================================================= */
/* Per-app analysis history drawer                               */
/* ============================================================= */
function AppHistoryDrawer({ appId, canPin, onClose, onChanged }: {
  appId: string; canPin: boolean; onClose: () => void; onChanged: () => void;
}) {
  const [data, setData] = useState<AppAnalyses | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // The panel has two views: the analysis list, and one analysis in full. Opening the
  // full analysis stays in this panel — Back returns to the list, X closes to the
  // main screen. Everything it shows is already loaded, so there's no extra fetch.
  const [fullItem, setFullItem] = useState<AnalysisHistoryItem | null>(null);
  const [comparing, setComparing] = useState(false);
  // The analysis the user is about to elect as final — drives the confirmation modal.
  // Held locally so the confirmation can show before anything is written.
  const [finalConfirm, setFinalConfirm] = useState<AnalysisHistoryItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { setData(await getAppAnalyses(appId)); }
    catch (e: any) { setErr(e?.response?.data?.error || e?.message || 'Failed to load history.'); }
    finally { setLoading(false); }
  }, [appId]);
  useEffect(() => { load(); }, [load]);

  const doPin = async (runAppId: string) => {
    setBusy(true); setErr('');
    try { await pinAnalysis(appId, runAppId); await load(); onChanged(); }
    catch (e: any) { setErr(e?.response?.data?.error || e?.message || 'Could not pin.'); }
    finally { setBusy(false); }
  };
  const doUnpin = async () => {
    setBusy(true); setErr('');
    try { await unpinAnalysis(appId); await load(); onChanged(); }
    catch (e: any) { setErr(e?.response?.data?.error || e?.message || 'Could not unpin.'); }
    finally { setBusy(false); }
  };
  // Commit the pending finalization (called from the confirmation modal). When
  // alsoRecord is set, the pinned analysis is also recorded AS the final disposition
  // (label + narrative) on the Final Disposition screen, in the same step.
  const confirmPinFinal = async (alsoRecord: boolean) => {
    if (!finalConfirm) return;
    setBusy(true); setErr('');
    try {
      await pinAnalysisAsFinal(appId, finalConfirm.run_app_id);
      if (alsoRecord) await recordFinalFromPinned(appId);
      setFinalConfirm(null); await load(); onChanged();
    }
    catch (e: any) { setErr(e?.response?.data?.error || e?.message || 'Could not set as final.'); }
    finally { setBusy(false); }
  };
  const doUnpinFinal = async () => {
    setBusy(true); setErr('');
    try { await unpinAnalysisAsFinal(appId); await load(); onChanged(); }
    catch (e: any) { setErr(e?.response?.data?.error || e?.message || 'Could not unpin final.'); }
    finally { setBusy(false); }
  };

  const app = data?.application;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000 }} />
      {/* Wider than a plain history drawer: it now carries the Q&A scoring breakdown
          (dimension → component → question) alongside the analyses. */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(900px, 96vw)', background: '#fff',
        zIndex: 1001, boxShadow: '-8px 0 30px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #eceff1', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {(fullItem || comparing) && (
              <button
                onClick={() => { setFullItem(null); setComparing(false); }}
                title="Back to this application's analyses"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, marginBottom: 6, color: '#1565c0', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
              >
                <i className="material-icons" style={{ fontSize: 16 }}>arrow_back</i>
                Back to analyses
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              {app?.application_id_code && <span style={{ fontWeight: 700, fontSize: 13, color: '#1565c0' }}>{app.application_id_code}</span>}
              <div style={{ fontWeight: 700, fontSize: 17, color: '#212121' }}>{app?.name || 'Application'}</div>
            </div>
            {app?.vendor && <div style={{ fontSize: 13, color: '#666', marginTop: 2 }}>{app.vendor}</div>}
            {!fullItem && !comparing && (
              <a href={`/customers/applications?appId=${appId}`} style={{ fontSize: 12, color: '#1565c0', textDecoration: 'none', fontWeight: 600, marginTop: 4, display: 'inline-block' }}>
                Open in Application Master Data →
              </a>
            )}
            {fullItem && (
              <div style={{ fontSize: 12, color: '#546e7a', marginTop: 4 }}>
                Full analysis · run <strong style={{ color: '#37474f' }}>{fullItem.run_name}</strong>
              </div>
            )}
            {comparing && (
              <div style={{ fontSize: 12, color: '#546e7a', marginTop: 4 }}>Comparing all analyses</div>
            )}
          </div>
          <button
            onClick={onClose} aria-label="Close" title="Close — back to the Analysis Repository"
            style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 9, border: '1.5px solid #dc2626', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <i className="material-icons" style={{ fontSize: 20 }}>close</i>
          </button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {err && <div style={{ fontSize: 13, color: '#c62828', marginBottom: 10 }}>{err}</div>}
          {fullItem ? (
            <FullAnalysisView item={fullItem} onBack={() => setFullItem(null)} />
          ) : comparing && data ? (
            <CompareView items={data.analyses} appId={appId} onBack={() => setComparing(false)} />
          ) : loading ? (
            <div style={{ color: '#9e9e9e', padding: 20 }}>Loading history…</div>
          ) : !data || data.analyses.length === 0 ? (
            <div style={{ color: '#9e9e9e', padding: 20 }}>No completed analyses for this application.</div>
          ) : (
            <>
              {/* The framework's own answer comes FIRST — a scored, evidence-based
                  disposition outranks advisory AI output, so it leads rather than
                  sitting among the analyses. Only shown once the Q&A is complete. */}
              {data.qa?.completed && (
                <QaDispositionSection
                  qa={data.qa}
                  appId={appId}
                  appName={data.application.name}
                  appCode={data.application.application_id_code || undefined}
                />
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: '#90a4ae' }}>
                  {data.analyses.length} analys{data.analyses.length === 1 ? 'is' : 'es'} on record ·
                  {data.pinnedRunAppId ? ' current is pinned' : ' none pinned'}
                </div>
                {data.analyses.length > 1 && (
                  <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
                    {/* One way in. This used to be two buttons that opened the same view —
                        the second only auto-ran the AI, which made re-entering it cost a
                        fresh call. The view now shows the diff plus whatever verdict is
                        already on record, and the AI runs only when asked. */}
                    <button
                      className="rd-btn rd-btn-info"
                      onClick={() => setComparing(true)}
                      style={{ fontSize: 12 }}
                      title="Every value side by side with the differences marked, plus the AI's view on which disposition is better grounded"
                    >
                      <i className="material-icons" style={{ fontSize: 16 }}>compare_arrows</i>
                      Disposition Comparison
                    </button>
                  </div>
                )}
              </div>
              {data.analyses.length > 1 && !data.pinnedRunAppId && (
                <div style={{ fontSize: 12, color: '#8a6d00', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '7px 10px', marginBottom: 12, lineHeight: 1.5 }}>
                  Nothing is pinned yet, so <strong>no analysis is the committed choice</strong>. Reports and portfolio totals fall back to the latest one — and a future run would silently take its place. Pin the one you trust to lock it in.
                </div>
              )}
              {/* Timeline */}
              <div style={{ position: 'relative', paddingLeft: 20 }}>
                <div style={{ position: 'absolute', left: 6, top: 6, bottom: 6, width: 2, background: '#eceff1' }} />
                {data.analyses.map((a) => {
                  // "Current" is only claimed when someone has actually pinned it.
                  // The API still resolves an unpinned app to its latest analysis for
                  // the list, totals and reports — but nothing here labels that a
                  // committed choice, because no one has made one.
                  const isPinned = !!data.pinnedRunAppId && a.run_app_id === data.pinnedRunAppId;
                  const isCurrent = isPinned;
                  const isFinal = !!data.finalRunAppId && a.run_app_id === data.finalRunAppId;
                  return (
                    <div key={a.run_app_id} style={{ position: 'relative', marginBottom: 16 }}>
                      <div style={{ position: 'absolute', left: -20, top: 6, width: 12, height: 12, borderRadius: '50%', background: isFinal ? '#a15c07' : (isCurrent ? '#5e35b1' : '#cfd8dc'), border: '2px solid #fff', boxShadow: '0 0 0 2px ' + (isFinal ? '#a15c07' : (isCurrent ? '#5e35b1' : '#cfd8dc')) }} />
                      <HistoryCard item={a} isCurrent={isCurrent} isPinned={isPinned} isFinal={isFinal} canPin={canPin} busy={busy}
                        onPin={() => doPin(a.run_app_id)} onUnpin={doUnpin}
                        onPinFinal={() => setFinalConfirm(a)} onUnpinFinal={doUnpinFinal}
                        onOpen={() => setFullItem(a)} />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {finalConfirm && (
        <FinalizeConfirm
          item={finalConfirm}
          appName={app?.name || 'this application'}
          qaDisposition={data?.qa?.completed ? (data?.qa?.disposition || null) : null}
          qaScore={data?.qa?.overall_score ?? null}
          replacingExisting={!!data?.finalRunAppId && data.finalRunAppId !== finalConfirm.run_app_id}
          busy={busy}
          onCancel={() => setFinalConfirm(null)}
          onConfirm={confirmPinFinal}
        />
      )}
    </>
  );
}

/**
 * The confirmation shown when electing an analysis as the disposition of record.
 * Pinning as final is a decision, not a display tweak, so it states plainly what is
 * being decided, whether it agrees with the Q&A answer, and what happens next.
 */
function FinalizeConfirm({ item, appName, qaDisposition, qaScore, replacingExisting, busy, onCancel, onConfirm }: {
  item: AnalysisHistoryItem; appName: string; qaDisposition: string | null; qaScore: number | string | null;
  replacingExisting: boolean; busy: boolean; onCancel: () => void; onConfirm: (alsoRecord: boolean) => void;
}) {
  const parent = parent4R(item.disposition);
  const qaParent = qaDisposition ? (parent4R(qaDisposition) || qaDisposition) : null;
  const agrees = !!qaParent && !!parent && qaParent === parent;
  const savingsMid = item.savings_mid != null ? `${fmt$(item.savings_mid)}/yr savings` : null;
  // Default ON: pinning as final and recording the decision is the common intent.
  const [alsoRecord, setAlsoRecord] = useState(true);
  return (
    <>
      <div onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100 }} />
      <div role="dialog" aria-modal="true" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(480px, 94vw)',
        background: '#fff', borderRadius: 14, zIndex: 1101, boxShadow: '0 12px 40px rgba(0,0,0,0.28)', overflow: 'hidden',
      }}>
        <div style={{ padding: '18px 20px 0' }}>
          <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#a15c07' }}>★ Set as final disposition</div>
          <h3 style={{ margin: '8px 0 4px', fontSize: 18, color: '#1e2733' }}>Make this the disposition of record for {appName}?</h3>
        </div>
        <div style={{ padding: '14px 20px 4px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ background: '#f4f6fa', border: '1px solid #dfe4ec', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
              <DispPill d={item.disposition} solid />
              <span style={{ fontSize: 12, color: '#55627a', textTransform: 'capitalize' }}>{item.origin === 'client_document' ? 'Client analysis' : 'Analysis'}{item.confidence ? ` · ${item.confidence} confidence` : ''}</span>
            </div>
            <div style={{ marginTop: 7, fontSize: 12, color: '#55627a' }}>
              {[savingsMid, item.disposition_timeline].filter(Boolean).join(' · ')}{item.run_name ? ` · from “${item.run_name}”` : ''}
            </div>
          </div>

          {qaParent ? (
            agrees ? (
              <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, background: '#e7f4e8', border: '1px solid rgba(46,125,50,0.3)', borderRadius: 10, padding: '10px 12px', color: '#1e2733' }}>
                <span style={{ color: '#2e7d32', fontWeight: 800 }}>✓</span>
                <div><strong>Consistent with the Q&A answer.</strong> Q&A scored {appName} <strong>{qaDisposition}{qaScore != null ? ` (${qaScore})` : ''}</strong>; {dispLabel(item.disposition)} is a {parent} path — evidence and framework agree.</div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, background: '#fff5e0', border: '1px solid #f3d9a0', borderRadius: 10, padding: '10px 12px', color: '#1e2733' }}>
                <span style={{ color: '#b26a00', fontWeight: 800 }}>⚠</span>
                <div><strong>This disagrees with the Q&A answer.</strong> Q&A scored {appName} <strong>{qaDisposition}{qaScore != null ? ` (${qaScore})` : ''}</strong>, but this analysis recommends <strong>{dispLabel(item.disposition)}</strong>{parent ? ` (a ${parent})` : ''}. You can still set it — the justification is recorded against the decision.</div>
              </div>
            )
          ) : null}

          <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13, color: '#55627a' }}>
            <span style={{ color: '#1565c0', fontWeight: 800 }}>→</span>
            <div><strong style={{ color: '#1e2733' }}>What happens next.</strong> {appName} appears on <strong>Final Disposition</strong> with {dispLabel(item.disposition)} and this analysis’s narrative as the justification. {alsoRecord ? <>It is recorded now as the decision of record — you can still change or override it there, and nothing transfers to Integration until you say so.</> : <>It is pre-filled there, ready for you to review and save.</>}</div>
          </div>

          {/* The "make the decision now" option. */}
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer', background: '#fdf8ee', border: '1px solid #edc98a', borderRadius: 10, padding: '10px 12px' }}>
            <input type="checkbox" checked={alsoRecord} onChange={(e) => setAlsoRecord(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer' }} />
            <span><strong style={{ color: '#1e2733' }}>Also record the final disposition now.</strong> Writes <strong>{dispLabel(item.disposition)}</strong> and its narrative straight to the Final Disposition screen. Leave unchecked to only pin the analysis and decide there later.</span>
          </label>
        </div>
        <div style={{ fontSize: 11.5, color: '#8a95a8', borderTop: '1px solid #e9edf3', marginTop: 6, padding: '11px 20px', background: '#f4f6fa' }}>
          {replacingExisting ? 'Only one analysis can be an app’s final one — this replaces the earlier pick. ' : ''}
          <strong style={{ color: '#55627a' }}>“Final” is separate from “Current”:</strong> Current controls what portfolio reports show today; Final is the decision you carry into integration planning.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, padding: '13px 20px' }}>
          <button className="rd-btn rd-btn-ghost" onClick={onCancel} disabled={busy} style={{ fontSize: 12.5 }}>Cancel</button>
          <button onClick={() => onConfirm(alsoRecord)} disabled={busy}
            style={{ fontSize: 12.5, fontWeight: 650, borderRadius: 8, padding: '8px 16px', cursor: busy ? 'default' : 'pointer', border: '1px solid #a15c07', background: '#a15c07', color: '#fff' }}>
            {busy ? 'Setting…' : (alsoRecord ? '★ Set as final & record' : '★ Pin as final')}
          </button>
        </div>
      </div>
    </>
  );
}

/**
 * The Q&A-driven initial disposition: what the framework itself concluded once every
 * question was answered, with the score behind it and the full Scoring Details
 * breakdown (collapsed — the panel leads with the answer, not the arithmetic).
 *
 * This is NOT part of the analyses list and never enters the AI comparison: it is a
 * scored result derived from evidence, where the analyses are advisory opinions.
 * Comparing the two would be a category error.
 */
function QaDispositionSection({ qa, appId, appName, appCode }: {
  qa: NonNullable<AppAnalyses['qa']>; appId: string; appName: string; appCode?: string;
}) {
  const [showDetails, setShowDetails] = useState(false);
  const score = qa.overall_score == null ? null : Number(qa.overall_score);
  return (
    <div style={{ border: '1px solid #c8e6c9', background: '#f4fbf5', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 800, color: '#2e7d32', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Initial Disposition · from Q&amp;A
        </span>
        <span style={{ fontSize: 11, color: '#557' }}>{qa.answered}/{qa.total} questions completed</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <DispPill d={qa.disposition} solid />
        {score != null && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#1b5e20' }}>{score.toFixed(2)}</span>
            <span style={{ fontSize: 11, color: '#557' }}>overall score (1.00–5.00)</span>
          </div>
        )}
        <button
          className="rd-btn rd-btn-ghost"
          onClick={() => setShowDetails((v) => !v)}
          style={{ fontSize: 12, marginLeft: 'auto' }}
          title="The dimension → component → question breakdown behind this score"
        >
          <i className="material-icons" style={{ fontSize: 16 }}>{showDetails ? 'expand_less' : 'expand_more'}</i>
          {showDetails ? 'Hide scoring details' : 'Scoring details'}
        </button>
      </div>

      <div style={{ fontSize: 11.5, color: '#557', marginTop: 8, lineHeight: 1.5 }}>
        Scored from the completed framework assessment — this is the framework's own answer. The analyses below are AI or client input and are compared separately.
      </div>

      {showDetails && (
        <div style={{ marginTop: 12, background: '#fff', border: '1px solid #e0eee2', borderRadius: 8, padding: 10 }}>
          <ScoringDetailsPanel
            applicationId={appId}
            applicationName={appName}
            applicationCode={appCode}
            embedded
          />
        </div>
      )}
    </div>
  );
}

/* Side-by-side comparison of every analysis on record for one app. Deterministic —
   it just lines the stored fields up and marks the ones that differ. No AI, no cost.
   This is what answers "which numbers actually disagree?" at a glance. */
function CompareView({ items, appId, onBack }: {
  items: AnalysisHistoryItem[]; appId: string; onBack: () => void;
}) {
  const money = (v: any) => fmt$(v);
  const [verdict, setVerdict] = useState<CompareVerdict | null>(null);
  const [verdictAt, setVerdictAt] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [asking, setAsking] = useState(false);
  const [askErr, setAskErr] = useState('');

  // Show the comparison already on record. This view is reached repeatedly (Back, then
  // in again), and the AI call costs ~30s — so re-entering must never re-run it. A new
  // comparison happens only when the user asks for one below.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = await getSavedComparison(appId);
        if (cancelled || !saved.verdict) return;
        setVerdict(saved.verdict);
        setVerdictAt(saved.createdAt || null);
        setStale(!!saved.stale);
      } catch { /* nothing saved yet — the user can run one */ }
    })();
    return () => { cancelled = true; };
  }, [appId]);

  const askAi = useCallback(async () => {
    setAsking(true); setAskErr('');
    try {
      const v = await askAiWhichIsRealistic(appId);
      setVerdict(v); setStale(false); setVerdictAt(new Date().toISOString());
    } catch (e: any) { setAskErr(e?.response?.data?.error || e?.message || 'The comparison failed.'); }
    finally { setAsking(false); }
  }, [appId]);
  const runNameOf = (id: string | null) => items.find((i) => i.run_app_id === id)?.run_name || null;
  const rows: { label: string; get: (a: AnalysisHistoryItem) => string; }[] = [
    { label: 'Disposition',      get: (a) => (a.disposition || '—') },
    { label: 'Confidence',       get: (a) => (a.confidence || '—') },
    { label: 'Current $/yr',     get: (a) => money(a.current_annual_cost) },
    { label: 'Savings (mid)',    get: (a) => money(a.savings_mid) },
    { label: 'Savings range',    get: (a) => `${money(a.savings_low)} – ${money(a.savings_high)}` },
    { label: 'Disposition cost', get: (a) => money(a.disposition_cost) },
    { label: 'Integration cost', get: (a) => money(a.integration_cost) },
    { label: 'Total one-time',   get: (a) => money(a.total_one_time_cost) },
    { label: 'Future run rate',  get: (a) => money(a.future_run_rate) },
    { label: 'Timeline',         get: (a) => (a.disposition_timeline || '—') },
  ];
  return (
    <div>
      <div style={{ fontSize: 12.5, color: '#546e7a', marginBottom: 12, lineHeight: 1.5 }}>
        Every value on record, side by side. <span style={{ color: '#8a6d00', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '0 4px' }}>Amber</span> marks
        a field the analyses don't agree on — that's where the choice actually matters.
      </div>
      {/* Fixed layout + an equal share per analysis, so the columns line up whatever
          the content length. Differences are marked with colour only — no bold. */}
      <div style={{ overflowX: 'auto' }}>
        <table className="rd-table" style={{ margin: 0, fontSize: 12.5, tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '22%' }} />
            {items.map((a) => <col key={a.run_app_id} style={{ width: `${78 / items.length}%` }} />)}
          </colgroup>
          <thead>
            <tr>
              <th style={{ fontWeight: 400 }}></th>
              {items.map((a) => (
                <th key={a.run_app_id} style={{ verticalAlign: 'top', fontWeight: 400 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <OriginTag origin={a.origin} />
                    <span style={{ fontWeight: 400, color: '#37474f', whiteSpace: 'normal' }}>{a.run_name}</span>
                    <span style={{ fontSize: 10, color: '#90a4ae', fontWeight: 400 }}>
                      {a.completed_at ? new Date(a.completed_at).toLocaleDateString() : ''}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const vals = items.map(row.get);
              const differs = new Set(vals).size > 1;
              return (
                <tr key={row.label}>
                  <td style={{ fontWeight: 400, color: '#546e7a' }}>{row.label}</td>
                  {vals.map((v, i) => (
                    <td key={i} style={{
                      background: differs ? '#fff8e1' : undefined,
                      color: differs ? '#5d4037' : '#37474f',
                      fontWeight: 400,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }}>{v}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* The numbers show WHAT differs; only judgement says which is better grounded. */}
      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #eceff1' }}>
        {!verdict && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button className="rd-btn rd-btn-info" onClick={askAi} disabled={asking} style={{ fontSize: 12 }}>
              <i className={`material-icons${asking ? ' rd-spin' : ''}`} style={{ fontSize: 16 }}>
                {asking ? 'refresh' : 'psychology'}
              </i>
              {asking ? 'Thinking…' : 'Disposition Comparison'}
            </button>
            <span style={{ fontSize: 11.5, color: '#90a4ae' }}>
              Reads the reasoning behind each disposition and says which is better grounded. Advice only — it changes nothing. ~30s.
            </span>
          </div>
        )}
        {askErr && <div style={{ fontSize: 12.5, color: '#c62828', marginTop: 8 }}>{askErr}</div>}

        {verdict && (
          <div style={{ background: '#f5f7ff', border: '1px solid #dbe3ff', borderRadius: 10, padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#3730a3', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                AI view
              </span>
              <span style={{ fontSize: 11, color: '#607d8b' }}>
                {verdict.confidence} confidence · advice only
                {verdictAt ? ` · ${new Date(verdictAt).toLocaleString()}` : ''}
              </span>
              <button className="rd-btn rd-btn-ghost" onClick={askAi} disabled={asking} style={{ fontSize: 11, marginLeft: 'auto' }}>
                <i className={`material-icons${asking ? ' rd-spin' : ''}`} style={{ fontSize: 14 }}>refresh</i>
                {asking ? 'Thinking…' : 'Run new comparison'}
              </button>
            </div>

            {/* The saved verdict predates one of the analyses now on record, so it can't
                have taken it into account. Say so rather than passing it off as current. */}
            {stale && (
              <div style={{ fontSize: 11.5, color: '#8a6d00', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '6px 9px', marginBottom: 8, lineHeight: 1.45 }}>
                This comparison was made from a different set of analyses than the ones now on record — run a new comparison to include them all.
              </div>
            )}

            <div style={{ fontSize: 13, color: '#1e293b', lineHeight: 1.55, marginBottom: 10 }}>{verdict.headline}</div>

            <div style={{ fontSize: 12, color: '#334155', marginBottom: 4 }}>
              <strong>Better grounded:</strong>{' '}
              {verdict.more_realistic_run_app_id
                ? (runNameOf(verdict.more_realistic_run_app_id) || 'one of these analyses')
                : 'too close to call'}
            </div>
            <div style={{ fontSize: 12.5, color: '#455a64', lineHeight: 1.55, marginBottom: 10 }}>{verdict.more_realistic_reason}</div>

            {verdict.differences.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#5e35b1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>What differs</div>
                {verdict.differences.map((d, i) => (
                  <div key={i} style={{ fontSize: 12.5, color: '#37474f', lineHeight: 1.5, marginBottom: 5 }}>
                    <strong>{d.field}:</strong> {d.summary} <span style={{ color: '#78909c' }}>— {d.why_it_matters}</span>
                  </div>
                ))}
              </div>
            )}

            {verdict.evidence_needed.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: '#5e35b1', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>What would settle it</div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12.5, color: '#37474f', lineHeight: 1.55 }}>
                  {verdict.evidence_needed.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}

            {verdict.consolidation_hint && (
              <div style={{ fontSize: 12.5, color: '#334155', background: '#fff', border: '1px solid #e3e8f5', borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 }}>
                <strong>If consolidated:</strong> {verdict.consolidation_hint}
              </div>
            )}
          </div>
        )}
      </div>

      <button className="rd-btn rd-btn-ghost" onClick={onBack} style={{ fontSize: 12, marginTop: 14 }}>
        <i className="material-icons" style={{ fontSize: 16 }}>arrow_back</i>
        Back to analyses
      </button>
    </div>
  );
}

/* The full analysis for ONE run, shown inside the same right-hand panel.
   Every field is already on the history item, so this needs no extra request. */
function FullAnalysisView({ item, onBack }: { item: AnalysisHistoryItem; onBack: () => void }) {
  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#5e35b1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#37474f', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{children}</div>
    </div>
  );
  const savings = (item.savings_low != null || item.savings_high != null)
    ? `${fmt$(item.savings_low)} – ${fmt$(item.savings_high)} (mid ${fmt$(item.savings_mid)})` : '—';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <DispPill d={item.disposition} solid />
        <OriginTag origin={item.origin} />
        {item.confidence && <span style={{ fontSize: 11, color: '#607d8b', textTransform: 'capitalize' }}>{item.confidence} confidence</span>}
        {item.mapped_framework_label && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#1565c0', background: '#e3f2fd', borderRadius: 20, padding: '2px 9px' }}>{item.mapped_framework_label}</span>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 8, marginBottom: 18 }}>
        <MiniStat label="Current $/yr" value={fmt$(item.current_annual_cost)} />
        <MiniStat label="Savings" value={savings} />
        <MiniStat label="One-time cost" value={fmtCostRange(item.total_one_time_cost, item.total_one_time_cost_low, item.total_one_time_cost_high)} />
        <MiniStat label="Future run rate" value={fmt$(item.future_run_rate)} />
        <MiniStat label="Disposition cost" value={fmtCostRange(item.disposition_cost, item.disposition_cost_low, item.disposition_cost_high)} />
        <MiniStat label="Integration cost" value={fmtCostRange(item.integration_cost, item.integration_cost_low, item.integration_cost_high)} />
      </div>

      {item.disposition_timeline && <Section title="Timeline">{item.disposition_timeline}</Section>}
      {item.rationale && <Section title="Rationale">{item.rationale}</Section>}
      {item.disposition_justification && <Section title="Justification">{item.disposition_justification}</Section>}
      {item.strategic_options && <Section title="Strategic options">{item.strategic_options}</Section>}
      {item.methodology_note && <Section title="Methodology">{item.methodology_note}</Section>}

      {!item.rationale && !item.disposition_justification && !item.strategic_options && (
        <div style={{ fontSize: 13, color: '#9e9e9e' }}>This analysis recorded no written rationale.</div>
      )}

      <button className="rd-btn rd-btn-ghost" onClick={onBack} style={{ fontSize: 12, marginTop: 4 }}>
        <i className="material-icons" style={{ fontSize: 16 }}>arrow_back</i>
        Back to analyses
      </button>
    </div>
  );
}

function HistoryCard({ item, isCurrent, isPinned, isFinal, canPin, busy, onPin, onUnpin, onPinFinal, onUnpinFinal, onOpen }: {
  item: AnalysisHistoryItem; isCurrent: boolean; isPinned: boolean; isFinal: boolean; canPin: boolean; busy: boolean;
  onPin: () => void; onUnpin: () => void; onPinFinal: () => void; onUnpinFinal: () => void; onOpen: () => void;
}) {
  const savingsRange = (item.savings_low != null || item.savings_high != null)
    ? `${fmt$(item.savings_low)} – ${fmt$(item.savings_high)} (mid ${fmt$(item.savings_mid)})` : '—';
  return (
    <div style={{
      border: `1px solid ${isFinal ? '#edc98a' : (isCurrent ? '#b39ddb' : '#e0e0e0')}`,
      background: isFinal ? '#fdf8ee' : (isCurrent ? '#f6f3ff' : '#fff'),
      boxShadow: isFinal ? '0 0 0 1px #edc98a' : undefined,
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <DispPill d={item.disposition} solid />
        <OriginTag origin={item.origin} />
        {item.confidence && <span style={{ fontSize: 11, color: '#607d8b', textTransform: 'capitalize' }}>{item.confidence} confidence</span>}
        {isFinal && <span title="Pinned as the disposition of record" style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.05em', color: '#fff', background: '#a15c07', borderRadius: 20, padding: '2px 8px', boxShadow: '0 0 0 3px #fdf0d5' }}>★ FINAL</span>}
        {isCurrent && <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.05em', color: '#fff', background: '#5e35b1', borderRadius: 20, padding: '2px 8px' }}>CURRENT</span>}
        {isPinned && <span title="Manually pinned" style={{ fontSize: 11 }}>📌 pinned</span>}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#90a4ae' }}>
          {item.completed_at ? new Date(item.completed_at).toLocaleDateString() : (item.created_at ? new Date(item.created_at).toLocaleDateString() : '')}
        </span>
      </div>
      <div style={{ fontSize: 12, color: '#546e7a', marginBottom: 6 }}>
        Run: <strong style={{ color: '#37474f' }}>{item.run_name}</strong>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, marginBottom: 8 }}>
        <MiniStat label="Current $" value={fmt$(item.current_annual_cost)} />
        <MiniStat label="Savings" value={savingsRange} />
        <MiniStat label="One-time cost" value={fmtCostRange(item.total_one_time_cost, item.total_one_time_cost_low, item.total_one_time_cost_high)} />
        <MiniStat label="Future run rate" value={fmt$(item.future_run_rate)} />
      </div>
      {/* Summary only. The timeline, rationale, justification, strategic options and
          methodology live in the full analysis (Open full analysis →) so this list
          stays scannable and you can pin from it without reading an essay. */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <button className="rd-btn rd-btn-ghost" onClick={onOpen} style={{ fontSize: 12 }}>Open full analysis →</button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {canPin && !isPinned && (
            <button className="rd-btn rd-btn-ghost" onClick={onPin} disabled={busy} style={{ fontSize: 12 }} title="Make this the current analysis (what reports show)">
              📌 Pin as current
            </button>
          )}
          {canPin && isPinned && (
            <button className="rd-btn rd-btn-ghost" onClick={onUnpin} disabled={busy} style={{ fontSize: 12 }} title="Revert to auto-latest">
              Unpin current
            </button>
          )}
          {canPin && !isFinal && (
            <button onClick={onPinFinal} disabled={busy}
              style={{ fontSize: 12, fontWeight: 650, borderRadius: 8, padding: '7px 12px', cursor: busy ? 'default' : 'pointer', border: '1px solid #a15c07', background: '#a15c07', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: 5 }}
              title="Elect this analysis as the disposition of record — carried into Final Disposition">
              <span style={{ fontWeight: 800 }}>★</span> Pin as final
            </button>
          )}
          {canPin && isFinal && (
            <button className="rd-btn rd-btn-ghost" onClick={onUnpinFinal} disabled={busy} style={{ fontSize: 12, borderColor: '#edc98a', color: '#a15c07' }} title="No longer the disposition of record">
              Unpin final
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: '#90a4ae', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#263238' }}>{value}</div>
    </div>
  );
}

export default AnalysisRepository;
